import { ApplicationAssetsLoader } from '@/application/ApplicationAssetLoader'
import { ApplicationLoader } from '@/application/ApplicationLoader'
import { ApplicationViewport } from '@/application/ApplicationViewport'
import { MainContainer } from '@/containers/MainContainer'
import Facade from '@/Facade'
import { DebugManager } from '@/managers/DebugManager'
import { MainManager } from '@/managers/MainManager'
import { SoundManager } from '@/managers/sound/SoundManager'
import { Model } from '@/model/Model'
import GameSignals from '@/signals/GameSignals'
import { gameRepo } from '@/repository/game'
import { translationRepo } from '@/repository/translation'
import { PlatformUtil } from '@/utils/PlatformUtil'
import * as PIXI from 'pixi.js'
import gsap from 'gsap'

export class Application {
    private static _initialized = false
    private _beforeLoadingHandled = false
    private _loadAssetsHandled = false
    private _preloaderAssetsHandled = false
    private _mainGameAssetsHandled = false
    private _mainContainer: MainContainer

    public constructor() {
        if (Application._initialized) {
            console.warn('[Application] Duplicate instance blocked')
            return
        }

        Application._initialized = true

        GameSignals.beforeLoading.connect(this.onBeforeLoading)
        GameSignals.loadAssets.connect(this.onLoadAssets)
        GameSignals.preloaderAssetsLoadingComplete.connect(this.onPreloaderAssetsLoadingComplete)
        GameSignals.mainGameAssetsLoadingComplete.connect(this.onMainGameAssetsLoadingComplete)
        GameSignals.splashScreenButtonClicked.connect(this.onSplashScreenButtonClicked)

        window.addEventListener('beforeunload', this.onTabClosing)
        window.addEventListener('close', this.onTabClosing)

        Facade.model = new Model()
        new MainManager(Facade.model)
        Facade.model.init()
    }

    private onTabClosing = () => {
        GameSignals.backendMessage.emit({ action: 'terminate' })
    }

    private onBeforeLoading = async () => {
        GameSignals.beforeLoading.disconnect(this.onBeforeLoading)

        if (this._beforeLoadingHandled) return
        this._beforeLoadingHandled = true

        await ApplicationLoader.loadGameFonts()

        GameSignals.beforeLoadingComplete.emit()
    }

    private onLoadAssets = async () => {
        GameSignals.loadAssets.disconnect(this.onLoadAssets)

        if (this._loadAssetsHandled) return
        this._loadAssetsHandled = true

        ApplicationAssetsLoader.registerLoaderMiddleware()
        await ApplicationLoader.loadPreloadingAssets()
    }

    private onPreloaderAssetsLoadingComplete = async () => {
        GameSignals.preloaderAssetsLoadingComplete.disconnect(this.onPreloaderAssetsLoadingComplete)

        if (this._preloaderAssetsHandled) return
        this._preloaderAssetsHandled = true

        if (!PlatformUtil.isWebGLSupported()) {
            GameSignals.hardwareAcceleration.emit()
            return
        }

        console.log('PreloaderAssets loaded')

        await translationRepo.getState().loadTranslate(gameRepo.getState().language)

        // Use PixiJS v8 Assets API instead of deprecated Loader
        Facade.displayConfig = PIXI.Assets.get('displayConfig')
        Facade.reelsConfig = PIXI.Assets.get('reelsConfig')
        Facade.anticipationConfig = PIXI.Assets.get('anticipationConfig')
        Facade.symbolsConfig = PIXI.Assets.get('symbolsConfig')
        Facade.winningsConfig = PIXI.Assets.get('winningsConfig')
        Facade.defaultConfig = PIXI.Assets.get('defaultConfig')

        ApplicationViewport.registerPixiHeaven()
        await ApplicationViewport.initializePixiViewport()
        ApplicationViewport.syncSpineWithPixiTicker()
        ApplicationViewport.registerInspector()

        ApplicationAssetsLoader.removeSpineMiddleware()

        ApplicationLoader.loadMainGameAssets()
    }

    private onMainGameAssetsLoadingComplete = () => {
        GameSignals.mainGameAssetsLoadingComplete.disconnect(this.onMainGameAssetsLoadingComplete)

        if (this._mainGameAssetsHandled) return
        this._mainGameAssetsHandled = true

        console.log('MainGameAssets loaded')

        SoundManager.initializeHowlerConfig(Facade.model)
        if (Facade.isDebug) {
            try {
                DebugManager.registerSuspenseConfig()
                DebugManager.registerReelsConfig()
                DebugManager.registerMultiplierConfig()
            } catch (error) {
                console.warn('[DebugManager] Debug panel not ready, skip debug registrations', error)
            }
        }

        this._mainContainer = new MainContainer()
        this._mainContainer.visible = false
        Facade.container.addChild(this._mainContainer)

        GameSignals.loadingProgress.emit(100)

        // Let the external preload UI paint the final 100% frame before the
        // splash/info pages are shown by the next loading state transition.
        requestAnimationFrame(() => {
            GameSignals.loadAssetsComplete.emit()
        })
    }

    private onSplashScreenButtonClicked = () => {
        // Vecchia intro: lasciava un delay artificiale dopo il click splash.
        // gsap.to(this, {
        //     duration: 2.5,
        //     onComplete: () => {
        //         GameSignals.completeSplashScreenState.emit(!Facade.model.isBonusGameNow)
        //     }
        // })

        GameSignals.completeSplashScreenState.emit(!Facade.model.isBonusGameNow)
        const isFreeSpinsRestore =
            !!Facade.model?.isFreeSpinsRestore ||
            (Number(Facade.model?.spinData?.freebet?.amount_left ?? 0) > 0 && !Facade.model?.isPromoSpinsRound?.())

        if (isFreeSpinsRestore) {
            GameSignals.showFreeSpinsBackground.emit()
            return
        }

        GameSignals.showBaseGameBackground.emit()
    }
}
